import { cfpBaseUrl } from "../network/urls";
import { defaultServices, defaultDurations, defaultServicesCFC } from "../utils/PortalName";

import FESCOLOGO from "../images/DISCO Logos/FESCO.png";
import GEPCOLOGO from "../images/DISCO Logos/GEPCO.png";
import HESCOLOGO from "../images/DISCO Logos/HESCO.png";
import IESCOLOGO from "../images/DISCO Logos/IESCO.png";
import MEPCOLOGO from "../images/DISCO Logos/MEPCO.png";
import LESCOLOGO from "../images/DISCO Logos/LESCO.jpg";
import PESCOLOGO from "../images/DISCO Logos/PESCO.png";
import QESCOLOGO from "../images/DISCO Logos/QESCO.png";
import SEPCOLOGO from "../images/DISCO Logos/SEPCO.png";
import TESCOLOGO from "../images/DISCO Logos/TESCO.png";
import HAZECOLOGO from "../images/DISCO Logos/HAZECO.jpg";
/**
 * Retrieves the subdivision name based on the given code from an array of subdivisions.
 *
 * @function subdivisionName
 * @memberof Services
 * @param {number} code - The subdivision code to look up.
 * @param {Array<Object>} array - The array containing subdivision objects.
 * @param {number} array[].subDivisionCode - The unique code of the subdivision.
 * @param {string} array[].subDivisionName - The name of the subdivision.
 * @returns {string} The name of the subdivision if found, otherwise returns the code as a string or a default value.
 */
export const subdivisionName = (code, array) => {
    if (code) {
        let subdivisionName = '';
        if (code !== 99999) {
            if (Array.isArray(array) && array.length > 0) { // Check if array is valid
                let subdivision = array.find(item => item.subDivisionCode == code);
                if (subdivision) {
                    subdivisionName = subdivision.subDivisionName;
                    return subdivisionName;
                } else {
                    return code.toString();
                }
            } else {
                console.error("Invalid or empty array passed to subdivisionName");
                return code.toString(); // Fallback to returning the code
            }
        }
    } else {
        return 'S Div Name';
    }
};

/**
 * Retrieves the division name based on the given code from an array of divisions.
 *
 * @function divisionName
 * @memberof Services
 * @param {number} code - The division code to look up.
 * @param {Array<Object>} array - The array containing division objects.
 * @param {number} array[].divisionCode - The unique code of the division.
 * @param {string} array[].divisionName - The name of the division.
 * @returns {string|number} The name of the division if found, otherwise returns the code.
 */
export const divisionName = (code, array) => {
    if (code) {
        let divisionName = '';
        let division = '';
        if (code !== 9999) {
            division = array.find(item => item.divisionCode == code)
            if (division) {
                divisionName = array.find(item => item.divisionCode == code).divisionName
            } else {
                divisionName = code
            }
        }
        else {
            divisionName = code
        }
        return divisionName
    }
}

/**
 * Retrieves the circle name based on the given code from an array of circles.
 *
 * @function circleName
 * @memberof Services
 * @param {number} code - The circle code to look up.
 * @param {Array<Object>} array - The array containing circle objects.
 * @param {number} array[].circleCode - The unique code of the circle.
 * @param {string} array[].circleName - The name of the circle.
 * @returns {string} The name of the circle if found, otherwise returns the code as a string or a default value.
 */
export const circleName = (code, array) => {
    if (code) {
        let circleName = '';
        if (code !== 99999) {
            let circle = array.find(item => item.circleCode == code)
            if (circle) {
                circleName = circle.circleName
                return circleName
            } else {
                return code.toString()
            }
        }
    } else {
        return 'Circle Name'
    }
}

/**
 * Retrieves the application name based on the given code.
 *
 * @function applicationName
 * @memberof Services
 * @param {number} code - The application code.
 * @returns {string} The corresponding application name or a default value if the code is not recognized.
 */
export const applicationName = (code) => {
    if (code) {
        switch (code) {
            case 1:
                return "New Connection"
            case 2:
                return "Change of Load"
            case 3:
                return "Change of Name"
            case 4:
                return "Change of Tariff"
            case 5:
                return "Change of Meter"
            case 13:
                return "Reconnection"
            default:
                return "Application Type"
        }
    }
}

/**
 * Validates a document file based on its extension and size.
 *
 * @function documentValidation
 * @memberof Services
 * @param {File} file - The file object to validate.
 * @param {number} maxSize - The maximum allowed file size in bytes.
 * @returns {number} Returns:
 *   - 1 if the file is valid.
 *   - 2 if the file size exceeds the maximum allowed size.
 *   - 3 if the file type is not allowed.
 */
export const documentValidation = (file, maxSize) => {
    let fileExtension = file.name.split(".").pop();

    if (["jpg", "png", "pdf", "JPG", "PNG", "jpeg", "JPEG", "PDF"].includes(fileExtension.toLowerCase())) {
        if (file.size <= maxSize) {
            return 1
        } else {
            return 2
        }
    } else {
        return 3
    }
}

/**
 * Checks if the input is an image file or a valid Base64 encoded image.
 *
 * @function isImage
 * @memberof Services
 * @param {File|string} input - The file object or Base64 string to check.
 * @returns {boolean} Returns `true` if the input is an image, otherwise `false`.
 */
export const isImage = (input) => {
    // Check if the input is already a File object
    if (input instanceof File) {
        return input.type.startsWith('image/');
    }

    // Check if the input is a Base64 string
    if (typeof input === 'string' && input.startsWith('data:image/')) {
        try {
            // Extract the MIME type and Base64 data
            const [mime, base64Data] = input.split(',');
            const mimeType = mime.match(/:(.*?);/)[1];

            // Decode the Base64 string to a binary string
            const byteString = atob(base64Data);
            const byteArray = new Uint8Array(byteString.length);

            // Convert binary string to an array of bytes
            for (let i = 0; i < byteString.length; i++) {
                byteArray[i] = byteString.charCodeAt(i);
            }

            // Create a File object from the byte array
            const file = new File([byteArray], 'image', { type: mimeType });
            return file.type.startsWith('image/');
        } catch (error) {
            console.error('Error while processing Base64 string:', error);
            return false;
        }
    }

    // If the input is neither a File object nor a valid Base64 string
    return false;
};

/**
 * Checks if the input is a PDF file or a valid Base64 encoded PDF.
 *
 * @function isPdf
 * @memberof Services
 * @param {File|string} input - The file object or Base64 string to check.
 * @returns {boolean} Returns `true` if the input is a PDF, otherwise `false`.
 */
export const isPdf = (input) => {
    // Check if the input is already a File object
    if (input instanceof File) {
        return input.type === 'application/pdf';
    }

    // Check if the input is a Base64 string for a PDF
    if (typeof input === 'string' && input.startsWith('data:application/pdf;')) {
        try {
            // Extract the MIME type and Base64 data
            const [mime, base64Data] = input.split(',');
            const mimeType = mime.match(/:(.*?);/)[1];

            // Decode the Base64 string to a binary string
            const byteString = atob(base64Data);
            const byteArray = new Uint8Array(byteString.length);

            // Convert binary string to an array of bytes
            for (let i = 0; i < byteString.length; i++) {
                byteArray[i] = byteString.charCodeAt(i);
            }

            // Create a File object from the byte array
            const file = new File([byteArray], 'document.pdf', { type: mimeType });
            return file.type === 'application/pdf';
        } catch (error) {
            console.error('Error while processing Base64 string:', error);
            return false;
        }
    }

    // If the input is neither a File object nor a valid Base64 string
    return false;
};

/**
 * Checks if a given Base64 string represents an image file based on its initial characters.
 *
 * @function isFileNameImage
 * @memberof Services
 * @param {string} base64Code - The Base64 string to check.
 * @returns {boolean} Returns `true` if the string represents an image, otherwise `false`.
 */
export const isFileNameImage = (base64Code) => {
    let extension = base64Code.slice(0, 6)
    return extension === 'data:i'
}

/**
 * Checks if a given Base64 string represents a PDF file based on its initial characters.
 *
 * @function isFileNamePdf
 * @memberof Services
 * @param {string} base64Code - The Base64 string to check.
 * @returns {boolean} Returns `true` if the string represents a PDF, otherwise `false`.
 */
export const isFileNamePdf = (base64Code) => {
    let extension = base64Code.slice(0, 6)
    return extension === 'data:a'
}

/**
 * Formats a given number with appropriate commas and decimal places.
 *
 * @function numberFormatting
 * @memberof Services
 * @param {number|string} number - The number to be formatted.
 * @returns {string|undefined} The formatted number with commas and at most one decimal place, or `undefined` if no number is provided.
 */
export const numberFormatting = (number) => {
    if (number) {
        let intNumber = number;

        if (typeof number === 'string') {
            intNumber = parseFloat(number);
        }

        const hasDecimal = intNumber % 1 !== 0;

        let formattedNumber = intNumber.toLocaleString(
            undefined,
            {
                minimumFractionDigits: hasDecimal ? 2 : 0,
                maximumFractionDigits: hasDecimal ? 2 : 0
            }
        );

        return formattedNumber;
    }
};


export const numberFormattingForRCO = (number) => {
  if (number !== null && number !== undefined) {
    let intNumber = Number(number);

    if (isNaN(intNumber)) return "";

    // Remove decimal WITHOUT rounding
    intNumber = intNumber;

    return intNumber.toLocaleString();
  }

  return "";
};


export const formatDate1 = (dateString) => {
    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
        return 'Invalid Date';
    }

    return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });
};



/**
 * Returns the background color based on the given flag value.
 *
 * @function getDocumentBackGroundColor
 * @memberof Services
 * @param {number|null} flagValue - The flag value determining the background color.
 * @returns {string} The corresponding background color.
 */
export const getDocumentBackGroundColor = (flagValue) => {

    switch (flagValue) {
        case null:
            return 'white'
        case 2:
            return 'white'
        case -1:
            return '#FFCAC9'
        case 0:
            return '#F3F6FB'
        case 1:
            return '#D6F6D5'
        default:
            return 'white'
    }
}

/**
 * Determines the status based on the given value and objection status.
 *
 * @function statusFunc
 * @memberof Services
 * @param {number} x - The main status value.
 * @param {number} objection - The objection flag (0 or 2 means no objection, otherwise objection exists).
 * @returns {string} The status description.
 */
export const statusFunc = (x, objection) => {
    if (objection === 0 || objection === 2) {
        if (x === 0) {
            return 'Pending'
        }
        else if (x > 0 && x < 4) {
            return 'In Process'
        }
        else if (x >= 4 && x < 8) {
            return 'Approved'
        }
        else {
            return 'Completed'
        }
    }
    else {
        return 'Objection'
    }
}

/**
 * Converts a date string into a formatted "DD-MMM-YYYY" format.
 *
 * @function convertDate
 * @memberof Services
 * @param {string} dateString - The date string to convert.
 * @returns {string} The formatted date in "DD-MMM-YYYY" format.
 */
export const convertDate = (dateString) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const date = new Date(dateString);
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
};

/**
 * Converts a date string into a formatted "DD-MMM-YY" format.
 *
 * @function convertDateDDMMYY
 * @memberof Services
 * @param {string} dateString - The date string to convert.
 * @returns {string} The formatted date in "DD-MMM-YY" format.
 */
export const convertDateDDMMYY = (dateString) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const date = new Date(dateString);
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear().toString()
    return `${day}-${month}-${year.slice(2)}`;
};

/**
 * Converts a date string into a formatted "YYYY-MM-DD" format.
 *
 * @function convertDateYYYYMMDD
 * @memberof Services
 * @param {string} dateString - The date string to convert.
 * @returns {string} The formatted date in "YYYY-MM-DD" format.
 */
export const convertDateYYYYMMDD = (dateString) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear().toString()
    return `${year}-${month}-${day}`;
};

/**
 * Checks if the given date is more than one year old.
 *
 * @function isOneYearOld
 * @memberof Services
 * @param {string} dateStr - The date string in "YYYY-MM-DD" format.
 * @returns {boolean} Returns `true` if the date is more than one year old, otherwise `false`.
 */
export const isOneYearOld = (dateStr) => {
    if (dateStr) {
        const [year, month, day] = dateStr.split('-');
        const inputDate = new Date(`${year}-${month}-${day}`);
        const currentDate = new Date();
        let diffInMs = currentDate - inputDate
        let diffInDays = diffInMs / (1000 * 60 * 60 * 24);
        return diffInDays > 365;
    } else {
        return false
    }
}

/**
 * Converts a date string into a formatted "DD-MMM-YYYY HH:MM:SS" format.
 *
 * @function convertDatewithTime
 * @memberof Services
 * @param {string} dateString - The date string to convert.
 * @returns {string} The formatted date in "DD-MMM-YYYY HH:MM:SS" format.
 */
export const convertDatewithTime = (dateString) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const date = new Date(dateString);
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    // Add leading zero if the value is a single digit
    const padZero = (value) => (value < 10 ? `0${value}` : value);

    const hour = padZero(date.getHours());
    const minute = padZero(date.getMinutes());
    const second = padZero(date.getSeconds());
    return `${day}-${month}-${year} ${hour}:${minute}:${second}`;
};

/**
 * Converts a date string into a formatted "DD-MMM-YYYY HH:MM" format (without seconds).
 *
 * @function convertDatewithTimeWithoutSecond
 * @memberof Services
 * @param {string} dateString - The date string to convert.
 * @returns {string} The formatted date in "DD-MMM-YYYY HH:MM" format.
 */
export const convertDatewithTimeWithoutSecond = (dateString) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const date = new Date(dateString);
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    // Add leading zero if the value is a single digit
    const padZero = (value) => (value < 10 ? `0${value}` : value);

    const hour = padZero(date.getHours());
    const minute = padZero(date.getMinutes());
    const second = padZero(date.getSeconds());
    return `${day}-${month}-${year} ${hour}:${minute}`;
};

/**
 * Returns the name of the electric supply company based on the given code.
 *
 * @function companyName
 * @memberof Services
 * @param {string} code - The company code.
 * @returns {string} The corresponding company name or a default name if not found.
 */
export const companyName = (code) => {
    switch (code) {
        case '10':
            return "POWER INFORMATION TECHNOLOGY COMPANY"
        case '11':
            return "Lahore Electric Supply Company"
        case '12':
            return "Gujranwala Electric Power Company"
        case '13':
            return "Faisalabad Electric Supply Company"
        case '14':
            return "Islamabad Electric Supply Company"
        case '15':
            return "Multan Electric Power Company"
        case '26':
            return "Peshawar Electric Supply Company"
        case '27':
            return "Hazara Electric Supply Company"
        case '38':
            return "Sukkur Electric Power Company"
        case '37':
            return "Hyderabad Electric Supply Company"
        case '48':
            return "Quetta Electric Supply Company"
        case '59':
            return "Tribal Electric Supply Company"
        case '91':
            return "Sundar Industrial Estate"
        default:
            return "DISTRIBUTION COMPANY NAME"
    }
};


export const getLogo = (code) => {
    switch (code) {
        case '11':
            return LESCOLOGO
        case '12':
            return GEPCOLOGO
        case '13':
            return FESCOLOGO
        case '14':
            return IESCOLOGO
        case '15':
            return MEPCOLOGO
        case '26':
            return PESCOLOGO
        case '27':
            return HAZECOLOGO
        case '37':
            return HESCOLOGO
        case '38':
            return SEPCOLOGO
        case '48':
            return QESCOLOGO
        case '59':
            return TESCOLOGO
        default:
            return MEPCOLOGO
    }
}
/**
 * Converts an object with sequentially numbered keys into an array of objects.
 *
 * @function convertToObjectArray
 * @memberof Services
 * @param {Object} data - The input object containing historical data.
 * @returns {Object[]} An array of formatted objects.
 */
export const convertToObjectArray = (data) => {
    const result = [];

    for (let i = 1; i <= 13; i++) {
        const entry = {
            ["gbHistMM"]: data["gbHistMM" + i] ? data["gbHistMM" + i] : '-',
            ["gbHistUnits"]: data["gbHistUnits" + i] ? data["gbHistUnits" + i] : "-",
            ["gbHistMDI"]: data["gbHistMDI" + i] ? data["gbHistMDI" + i] : "-",
            ["payment"]: data["payment" + i] ? numberFormatting(data["payment" + i]) : '-',
            ["gbHistAssment"]: data["gbHistAssment" + i] ? numberFormatting(data["gbHistAssment" + i]) : '-',
        };

        result.push(entry);
    }

    return result;
};

/**
 * Retrieves the name associated with a given code from an array of objects.
 *
 * @function getName
 * @memberof Services
 * @param {string|number} code - The code to search for.
 * @param {Object[]} array - The array of objects containing company, circle, division, or sub-division data.
 * @returns {string} The corresponding name, or a default category name if not found.
 */
export const getName = (code, array) => {
    if (typeof code != "string") {
        code = code.toString()
    }

    let codeLength = code.length

    if (codeLength === 2) {
        let company = array.find((item) => (item.companyCode == code))
        if (company) {
            return company.companyName
        } else {
            return 'Company'
        }
    }
    if (codeLength === 3) {
        let circle = array.find((item) => (item.circleCode == code))
        if (circle) {
            return circle.circleName
        } else {
            return 'Circle'
        }
    }
    if (codeLength === 4) {
        let division = array.find((item) => (item.divisionCode == code))
        if (division) {
            return division.divisionName
        } else {
            return 'Division'
        }
    }
    if (codeLength === 5) {
        let subDivision = array.find((item) => (item.subDivisionCode == code))
        if (subDivision) {
            return subDivision.subDivisionName
        } else {
            return 'Sub-Division'
        }
    }
};

/**
 * Extracts unique values from an array based on a specific key and hierarchical level.
 *
 * @function uniqueCode
 * @memberof Services
 * @param {Object[]} array - The input array of objects.
 * @param {string} key - The key to filter unique values by.
 * @param {number} level - The hierarchy level to determine the key extraction logic.
 * @returns {string[]} An array of unique values.
 */
export const uniqueCode = (array, key, level) => {
    const uniqueValues = new Set();
    const uniqueArray = [];
    if (level === 1) {
        array.forEach((obj) => {
            if (!uniqueValues.has(obj[key])) {
                uniqueValues.add(obj[key]);
                uniqueArray.push(obj.companyCode);
            }
        });
    }
    else if (level === 2) {
        array.forEach((obj) => {
            if (!uniqueValues.has(obj[key])) {
                uniqueValues.add(obj[key]);
                uniqueArray.push(obj.circleCode);
            }
        });
    }
    else if (level === 3) {
        array.forEach((obj) => {
            if (!uniqueValues.has(obj[key])) {
                uniqueValues.add(obj[key]);
                uniqueArray.push(obj.divisionCode);
            }
        });
    }
    else if (level === 4) {
        array.forEach((obj) => {
            if (!uniqueValues.has(obj[key])) {
                uniqueValues.add(obj[key]);
                uniqueArray.push(obj.subDivisionCode);
            }
        });
    }
    else if (level === 5) {
        array.forEach((obj) => {
            if (!uniqueValues.has(obj[key])) {
                uniqueValues.add(obj[key]);
                uniqueArray.push(obj.subDivisionCode);
            }
        });
    }
    return uniqueArray;
};

/**
 * Determines the parent level of an office hierarchy.
 *
 * @function getParentLevel
 * @memberof Services
 * @param {number} officeHierarchy - The hierarchy level.
 * @returns {number|null} The parent level or `null` if not defined.
 */
export const getParentLevel = (officeHierarchy) => {
    switch (officeHierarchy) {
        case 1:
            return 2;
        case 2:
            return 2;
        case 3:
            return 3;
        case 4:
            return 4;
        case 51:
            return 4;
        case 52:
            return 5;
        default:
            return null;
    }
};

/**
 * Determines the child level of an office hierarchy.
 *
 * @function getChildLevel
 * @memberof Services
 * @param {number} officeHierarchy - The hierarchy level.
 * @returns {number|null} The child level or `null` if not defined.
 */
export const getChildLevel = (officeHierarchy) => {
    switch (officeHierarchy) {
        case 1:
            return 3;
        case 2:
            return 3;
        case 3:
            return 4;
        case 4:
            return 5;
        case 51:
            return 5;
        case 52:
            return 5;
        default:
            return null;
    }
};

/**
 * Determines whether the first value is greater than the second value.
 *
 * @function getTrendingIcon
 * @memberof Services
 * @param {number} value1 - The first value.
 * @param {number} value2 - The second value.
 * @returns {boolean} `true` if value1 is greater than value2, otherwise `false`.
 */
export const getTrendingIcon = (value1, value2) => {
    return value1 > value2
}

/**
 * Returns the short name of the electricity distribution company based on the provided code.
 * @function companyShortName
 * @memberof Services
 * @param {string} code - The company code.
 * @returns {string} The short name of the company.
 */
export const companyShortName = (code) => {
    switch (code) {
        case "10":
            return "PITC";
        case "11":
            return "LESCO";
        case "12":
            return "GEPCO";
        case "13":
            return "FESCO";
        case "14":
            return "IESCO";
        case "15":
            return "MEPCO";
        case "26":
            return "PESCO";
        case "27":
            return "HAZECO";
        case "37":
            return "HESCO";
        case "38":
            return "SEPCO";
        case "48":
            return "QESCO";
        case "59":
            return "TESCO";
        case "91":
            return "SUNDAR";
        default:
            return "DISTRIBUTION COMPANY NAME";
    }
};

/**
 * Retrieves the Google Maps API key for a given electricity company code.
 * @function getGoogleMapsApiKey
 * @memberof Services
 * @param {string} code - The company code.
 * @returns {string|undefined} The Google Maps API key.
 */
export const getGoogleMapsApiKey = (code) => {
    switch (code) {
        case "10":
            return process.env.REACT_APP_MEPCO_MAP_KEY;
        case "11":
            return process.env.REACT_APP_LESCO_MAP_KEY;
        case "12":
            return process.env.REACT_APP_GEPCO_MAP_KEY;
        case "13":
            return process.env.REACT_APP_FESCO_MAP_KEY;
        case "14":
            return process.env.REACT_APP_IESCO_MAP_KEY;
        case "15":
            return process.env.REACT_APP_MEPCO_MAP_KEY;
        case "26":
            return process.env.REACT_APP_PESCO_MAP_KEY;
            case "27":
            return process.env.REACT_APP_PESCO_MAP_KEY;
        case "37":
            return process.env.REACT_APP_HESCO_MAP_KEY;
        case "38":
            return process.env.REACT_APP_SEPCO_MAP_KEY;
        case "48":
            return process.env.REACT_APP_QESCO_MAP_KEY;
        case "59":
            return process.env.REACT_APP_TESCO_MAP_KEY;
        default:
            return process.env.REACT_APP_MAP_API_KEY;
    }
};

/**
 * Determines the company name based on the starting digits of the provided code.
 * @function getCompanyName
 * @memberof Services
 * @param {string} code - The company code.
 * @returns {string} The company name.
 */
export const getCompanyName = (code) => {
    if (code.startsWith("11")) {
        return "LESCO";
    } else if (code.startsWith("12")) {
        return "GEPCO";
    } else if (code.startsWith("13")) {
        return "FESCO";
    } else if (code.startsWith("14")) {
        return "IESCO";
    } else if (code.startsWith("15")) {
        return "MEPCO";
    } else if (code.startsWith("26")) {
        return "PESCO";
    }else if (code.startsWith("27")) {
        return "HAZECO";
    } else if (code.startsWith("37")) {
        return "HESCO";
    }
    else if (code.startsWith("38")) {
        return "SEPCO";
    }
    else if (code.startsWith("48")) {
        return "QESCO";
    }
    else if (code.startsWith("49")) {
        return "TESCO";
    }
    else {
        return "MEPCO";
    }
}

/**
 * Retrieves the reason for meter change based on the reason code.
 * @function getMeterChangeReason
 * @memberof Services
 * @param {string} reasonCode - The reason code.
 * @returns {string} The corresponding meter change reason.
 */
export const getMeterChangeReason = (reasonCode) => {
    switch (reasonCode) {
        case '1':
            return 'No Display'
        case '2':
            return 'Meter Terminals Burnt'
        case '3':
            return 'Meter Stolen'
        default:
            return 'Unknown'
    }
};

/**
 * Formats a date string from "YYYY-MM-DD" to "DD-MM-YYYY".
 * @function formatDateToDDMMYYYY
 * @memberof Services
 * @param {string} inputDate - The input date in "YYYY-MM-DD" format.
 * @returns {string|null} The formatted date in "DD-MM-YYYY" format, or `null` if input is invalid.
 */
export const formatDateToDDMMYYYY = (inputDate) => {
    if (inputDate) {
        const parts = inputDate.split('-');
        const formattedDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
        return formattedDate
    }
}

/**
 * Handles file upload validation, renames the file, and generates a thumbnail.
 * @function handleFileUpload
 * @memberof Services
 * @param {Event} event - The file input event.
 * @param {Function} handleChange - Function to handle the file change.
 * @param {Function} setState - Function to set the file state.
 * @param {Function} setError - Function to set error messages.
 * @param {Function} setThumbnail - Function to set the file preview.
 * @param {string} newFileName - The new filename for the uploaded file.
 * @returns {void}
 */
export const handleFileUpload = async (event, handleChange, setState, setError, setThumbnail, newFileName) => {
    handleChange(event);
    const file = event.target.files[0];
    if (!file) return;
    const maxSize = 2 * 1024 * 1024; // 2MB in bytes
    const documentValidate = documentValidation(file, maxSize);
    if (documentValidate !== 1) { setError(documentValidate === 2 ? `File size exceeds ${maxSize / 1024 / 1024}MB limit.` : "Invalid file type. Only JPG, PNG, and PDF are allowed."); return; }
    const renamedFile = new File([file], newFileName, { type: file.type });
    setState(renamedFile);
    const reader = new FileReader();
    reader.onload = (e) => setThumbnail(e.target.result);
    reader.readAsDataURL(file);
    setError('')
};

/**
 * @function subDivAPI
 * @description Fetches the subdivision hierarchy details for a given company.
 * @memberof Services
 * 
 * @param {number|string} userCompany - The company code of the user.
 * @param {string} cfpBaseUrl - The base URL of the Customer Facilitation Portal API.
 * @param {string} token - Authorization token for API requests.
 * @returns {Promise<Object>} - A promise resolving to an object containing hierarchyList, circleList, divisionList, and subDivisionList.
 */
export const subDivAPI = async (subDivisions) => {

    let lists = {
        circleList: [],
        divisionList: [],
        subDivisionList: []
    }
    // if (!userCompany) return;

    // const companyCode = [11, 12, 13, 15, 26, 37, 38, 48, 59].includes(parseInt(userCompany)) ? parseInt(userCompany) : 15;

    // const formdata = new FormData();
    // formdata.append("companyCode", companyCode);

    // const requestOptions = {
    //     method: "POST",
    //     body: formdata,
    //     headers: new Headers({ "Authorization": token }),
    //     redirect: "follow",
    // };

    try {
        // const response = await fetch(cfpBaseUrl + "customerFaciliationPortal/subDivWithCompDetails", requestOptions);
        const result = subDivisions

        const sortedSdArray = result.sort((a, b) => (a.subDivisionName || '').toUpperCase() - (b.subDivisionName || '').toUpperCase());

        // Extract unique circle codes
        const uniqueCircleCodes = [...new Set(sortedSdArray.map(sd => sd.circleCode))];
        // Extract unique division codes
        const uniqueDivisionCodes = [...new Set(sortedSdArray.map(sd => sd.divisionCode))];
        // Extract unique subdivision codes
        const uniqueSubDivisionCodes = [...new Set(sortedSdArray.map(sd => sd.subDivisionCode))];

        // Filter out duplicate circle names
        const circleList = uniqueCircleCodes.map(circleCode => {
            const circle = sortedSdArray.find(sd => sd.circleCode === circleCode);
            return { code: circleCode, name: circle.circleName };
        });

        // Filter out duplicate division names
        const divisionList = uniqueDivisionCodes.map(divisionCode => {
            const division = sortedSdArray.find(sd => sd.divisionCode === divisionCode);
            return { code: divisionCode, name: division.divisionName };
        });

        // Filter out duplicate subdivision names
        const subDivisionList = uniqueSubDivisionCodes.map(subDivisionCode => {
            const subDivision = sortedSdArray.find(sd => sd.subDivisionCode === subDivisionCode);
            return { code: subDivisionCode, name: subDivision.subDivisionName };
        });
        lists = {
            hierarchyList: sortedSdArray,
            circleList: circleList,
            divisionList: divisionList,
            subDivisionList: subDivisionList
        }
    }
    catch (error) {
        console.log("error", error);
    }
    return lists
}

/**
 * @function allCompaniesAPI
 * @description Fetches all companies available in the system.
 * @memberof Services
 * 
 * @param {string} token - Authorization token for API requests.
 * @param {string} cfpBaseUrl - The base URL of the Customer Facilitation Portal API.
 * @returns {Promise<Array>} - A promise resolving to an array of company details.
 */
export const allCompaniesAPI = async (token, cfpBaseUrl) => {
    let companies = []
    try {

        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);

        const formdata = new FormData();

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: formdata,
            redirect: "follow"
        };

        await fetch(cfpBaseUrl + "customerFaciliationPortal/getCompanyDetails", requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (result.status === "200") {
                    let companiesList = result.data
                    companies = companiesList
                }
            })
            .catch((error) => console.error(error));
    }
    catch (e) {
        console.log(e)
    }
    return companies
}

/**
 * @function allCFCLevelsAPI
 * @description Fetches all available CFC levels.
 * @memberof Services
 * 
 * @param {string} token - Authorization token for API requests.
 * @param {string} cfpBaseUrl - The base URL of the Customer Facilitation Portal API.
 * @returns {Promise<Array>} - A promise resolving to an array of CFC levels.
 */
export const allCFCLevelsAPI = async (token, cfpBaseUrl) => {
    let cfcLevelsList = []
    try {

        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);

        const formdata = new FormData();

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: formdata,
            redirect: "follow"
        };

        await fetch(cfpBaseUrl + "customerFaciliationPortal/getCFCLevels", requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (result.status === "200") {
                    let cfcLevels = result.data
                    cfcLevelsList = cfcLevels
                }
            })
            .catch((error) => console.error(error));
    }
    catch (e) {
        console.log(e)
    }
    return cfcLevelsList
}

/**
 * @function allEmployeeRoleTypes
 * @description Fetches all employee role types.
 * @memberof Services
 * 
 * @param {string} token - Authorization token for API requests.
 * @param {string} cfpBaseUrl - The base URL of the Customer Facilitation Portal API.
 * @returns {Promise<Array>} - A promise resolving to an array of employee role types.
 */
export const allEmployeeRoleTypes = async (token, cfpBaseUrl) => {
    let allEmployeeRoleTypes = []
    try {

        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);

        const formdata = new FormData();

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: formdata,
            redirect: "follow"
        };

        await fetch(cfpBaseUrl + "role/getAllEmployeeRoleTypes", requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (result.status === "200") {
                    allEmployeeRoleTypes = result.data
                }
            })
            .catch((error) => console.error(error));
    }
    catch (e) {
        console.log(e)
    }
    return allEmployeeRoleTypes
}

/**
 * @function printStamp
 * @description Generates an office stamp image with the given details.
 * @memberof Services
 * 
 * @param {string} officeCode - The office code.
 * @param {string} officeName - The office name.
 * @param {string} officerName - The officer's name.
 * @param {string} designation - The designation of the officer.
 * @param {string} dateToPrint - The date to print on the stamp.
 * @returns {string} - A base64 encoded image string of the generated stamp.
 */
export const printStamp = (officeCode, officeName, officerName, designation, dateToPrint) => {
    // Create a canvas element
    var canvas = document.createElement('canvas');
    canvas.width = 350;
    canvas.height = 200;
    var ctx = canvas.getContext('2d');

    // Set canvas background to transparent
    ctx.fillStyle = 'rgba(20, 20, 20, 0)'; // Transparent background
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = 'black'; // Text color

    // Position base values
    var x = 35;
    var y = 25;
    var lineGap = 18; // consistent vertical spacing between lines
    var slash = ' - ';

    // Slight rotation for stamp effect
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((-10 * Math.PI) / 180);
    ctx.translate(-canvas.width / 2, -canvas.height / 2);

    // 1️⃣ Approved By + Designation
    ctx.font = '16px Gotham';
    ctx.fillText('Approved By:', x + 5, y);
    ctx.font = 'bold 16px Gotham';
    ctx.fillText(designation, x + 5, y + lineGap);

    // 2️⃣ Officer Name
    ctx.font = '16px Gotham';
    ctx.fillText(officerName, x + 5, y + lineGap * 2);

    // 3️⃣ Office Code / Name
    ctx.font = '20px Digital7';
    ctx.fillText(officeCode, x + 5, y + lineGap * 3);
    ctx.font = '18px Gotham';
    ctx.fillText(slash, x + 50, y + lineGap * 3);
    ctx.fillText(officeName, x + 70, y + lineGap * 3);

    // 4️⃣ Date
    ctx.font = '16px Digital7';
    ctx.fillText(dateToPrint, x + 5, y + lineGap * 4);

    // 🟦 Draw rounded rectangle around all text
    var maxWidth = Math.max(
        ctx.measureText(`${officeCode}${slash}${officeName}`).width,
        ctx.measureText(officerName).width,
        ctx.measureText(designation).width
    ) + 40;

    var borderRadius = 10;
    var boxTop = y - 20;
    var boxBottom = y + lineGap * 4 + 10;

    ctx.strokeStyle = 'black';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(x + borderRadius, boxTop);
    ctx.lineTo(x + maxWidth, boxTop);
    ctx.arcTo(x + maxWidth + borderRadius, boxTop, x + maxWidth + borderRadius, boxTop + borderRadius, borderRadius);
    ctx.arcTo(x + maxWidth + borderRadius, boxBottom, x, boxBottom, borderRadius);
    ctx.arcTo(x, boxBottom, x, boxTop, borderRadius);
    ctx.arcTo(x, boxTop, x + maxWidth, boxTop, borderRadius);
    ctx.closePath();
    ctx.stroke();

    // Reset transformation
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    return canvas.toDataURL('image/png');
};



/**
 * @function timeFrameNewConnection
 * @description Fetches the new connection time frame details.
 * @memberof Services
 * 
 * @param {string} token - Authorization token for API requests.
 * @returns {Promise<Array>} - A promise resolving to an array containing the time frame details for a new connection.
 */
export const timeFrameNewConnection = async (token) => {
    let newConnectionTimeFrame = []
    try {
        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            redirect: "follow"
        };

        await fetch(cfpBaseUrl + "comman/newConnectioTimeFrame", requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (result.status === "200") {
                    newConnectionTimeFrame = result.data
                }
            })
            .catch((error) => console.error(error));
    } catch (e) {
        console.log(e, 'error getting new connection timeframe')
    }
    return newConnectionTimeFrame
}

/**
 * @function sodowo
 * @description Determines the relational prefix (e.g., Son of, Daughter of, Wife of) based on the given input.
 * @memberof Services
 * 
 * @param {number|string} x - The input value representing the relation type (1, 2, 3) or a descriptive string.
 * @returns {string} - The corresponding relation type text.
 */
export const sodowo = (x) => {
    return ((x == 1 || x == 2) || (x == `S/O` || x == `D/O`)) ? "Father's Name" : (x == 3 || x == `W/O`) ? "Husband's Name" : (x == 4 || x == `C/O`) ? 'Care of Name' : 'Relation Type'
}

/**
 * @function buildTree
 * @description Recursively builds a hierarchical tree structure from a flat array of data.
 * @memberof Services
 * 
 * @param {Array<Object>} data - The input data array containing nodes with `id` and `parent_id` properties.
 * @param {number|null} [rootId=null] - The ID of the root node (defaults to null).
 * @returns {Array<Object>} - A tree-structured array of objects with nested children.
 */
export const buildTree = (data, rootId = null) => {
    const tree = [];
    if (data) {
        data.forEach(node => {
            if (node.parent_idt === rootId) {
                const children = buildTree(data, node.idt);
                if (children.length > 0) {
                    node.children = children;
                }
                tree.push(node);
            }
        });
    }
    return tree;
}

/**
 * @function getCenterAndZoom
 * @description Calculates the center and zoom level of a set of geographical points using Leaflet.
 * @memberof Services
 * 
 * @param {Array<Object>} data - Array of objects containing `latitude` and `longitude` properties as strings.
 * @param {Object} L - The Leaflet library object.
 * @returns {Object} - An object containing the calculated `center` (latitude, longitude) and `zoom` level.
 */
export const getCenterAndZoom = (data, L) => {
    // Convert string coordinates to floats
    const coordinates = data.map(point => ({
        lat: parseFloat(point.latitude),
        lng: parseFloat(point.longitude)
    }));

    // Calculate the bounding box
    const bounds = coordinates.reduce(
        (acc, coord) => {
            acc.minLat = Math.min(acc.minLat, coord.lat);
            acc.maxLat = Math.max(acc.maxLat, coord.lat);
            acc.minLng = Math.min(acc.minLng, coord.lng);
            acc.maxLng = Math.max(acc.maxLng, coord.lng);
            return acc;
        },
        {
            minLat: Infinity,
            maxLat: -Infinity,
            minLng: Infinity,
            maxLng: -Infinity
        }
    );

    // Calculate the center point
    const center = {
        lat: (bounds.minLat + bounds.maxLat) / 2,
        lng: (bounds.minLng + bounds.maxLng) / 2
    };

    // Assuming you're using Leaflet or similar to calculate the zoom level
    // Create a Leaflet LatLngBounds object
    const latLngBounds = L.latLngBounds(
        L.latLng(bounds.minLat, bounds.minLng),
        L.latLng(bounds.maxLat, bounds.maxLng)
    );

    // Create a Leaflet map (this should be your map instance)
    const map = L.map('map').setView(center, 13); // Initialize with some default center and zoom

    // Fit the bounds to the map
    map.fitBounds(latLngBounds);

    // You can return or use the center and calculated zoom level
    return { center, zoom: map.getZoom() };
}

/**
 * @function connectionTypes
 * @description Returns a description of a connection issue based on the given ID.
 * @memberof Services
 * 
 * @param {number} id - The ID representing a specific connection issue.
 * @returns {string} - The corresponding connection issue description.
 */
export const connectionTypes = (id) => {

    if (id === 1) {
        return 'Power Outage';
    }
    else if (id === 6) {
        return 'Low Voltage';
    }
    else if (id === 4) {
        return 'Fluctuation';
    }
    else if (id === 2) {
        return 'Defective Meter';
    }
    else if (id === 8) {
        return 'Phase Issue';
    }
    else if (id === 14) {
        return 'Live Fallen Wire';
    }
    else if (id === 32) {
        return 'Wrong Meter Reading';
    }
    else if (id === 33) {
        return 'Delayed / No Meter Reading';
    }
    else if (id === 22) {
        return 'First Bill Not Received';
    }
    else if (id === 21) {
        return 'Unjustified Detection Bill';
    }
    else {
        return 'Power Outage'
    }

}
const publicHolidays = [
    // Month-Date (year is ignored in isPublicHoliday)
    new Date('2026-01-01'), // New Year Bank Holiday
    new Date('2026-02-05'), // Kashmir Day
    new Date('2026-03-20'), // Pakistan Day
    new Date('2026-03-23'), // Pakistan Day
    new Date('2026-05-01'), // Labour Day
    new Date('2026-07-01'), // Bank Holiday
    new Date('2026-08-14'), // Independence Holiday
    new Date('2026-11-09'), // Iqbal Day
    new Date('2026-12-25'), // Quaid-e-Azam Day
];

/**
 * Determines if a given date is a public holiday.
 * This function checks the provided date against a predefined list of public holidays.
 * 
 * @memberof Services
 * @function isPublicHoliday
 * @param {Date} date - The date to check.
 * @returns {boolean} - Returns `true` if the date is a public holiday, otherwise `false`.
 */
export const isPublicHoliday = (date) => {
    return publicHolidays.some(holiday =>
        holiday.getMonth() === date.getMonth() &&
        holiday.getDate() === date.getDate()
    );
};
/**
 * Adds a specified number of business days to a given start date, 
 * skipping weekends (Saturday and Sunday) and public holidays.
 * 
 * @memberof Services
 * @function addBusinessDays
 * @param {Date} startDate - The starting date.
 * @param {number} daysToAdd - The number of business days to add.
 * @returns {Date} - The resulting date after adding the specified business days.
 * 
 * @example
 * const newDate = addBusinessDays(new Date('2024-03-25'), 5);
 * console.log(newDate); // Returns the date after adding 5 business days
 */
export const addBusinessDays = (startDate, daysToAdd) => {
    const resultDate = new Date(startDate);
    const originalStartDate = new Date(startDate);

    // Add the specified number of calendar days (includes weekends and holidays)
    resultDate.setDate(resultDate.getDate() + daysToAdd);

    // Check if result is a weekend
    if (resultDate.getDay() === 0 || resultDate.getDay() === 6) {
        // Calculate forward date (next working day)
        const forwardDate = new Date(resultDate);
        while (forwardDate.getDay() === 0 || forwardDate.getDay() === 6) {
            forwardDate.setDate(forwardDate.getDate() + 1);
        }

        // Calculate days difference if we go forward
        const diffTime = forwardDate - originalStartDate;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        // If forward would exceed 10 days, go backward instead
        if (diffDays > 10) {
            // Move backward to last working day
            while (resultDate.getDay() === 0 || resultDate.getDay() === 6 || isPublicHoliday(resultDate)) {
                resultDate.setDate(resultDate.getDate() - 1);
            }
        } else {
            // Move forward to next working day
            while (resultDate.getDay() === 0 || resultDate.getDay() === 6) {
                resultDate.setDate(resultDate.getDate() + 1);
            }
        }
    }

    // For 10 days, also check public holidays
    if (daysToAdd === 10) {
        while (resultDate.getDay() === 0 || resultDate.getDay() === 6 || isPublicHoliday(resultDate)) {
            resultDate.setDate(resultDate.getDate() - 1);
        }
    }

    return resultDate;
};



/**
 * Calculates the number of remaining business days in the current month 
 * from a given start date. Excludes weekends and public holidays.
 * 
 * @memberof Services
 * @function calculateRemainingBusinessDays
 * @param {Date} startDate - The starting date.
 * @param {Date[]} publicHolidays - An array of public holidays.
 * @returns {number} - The number of remaining business days in the month.
 * 
 * @example
 * const remainingDays = calculateRemainingBusinessDays(new Date('2024-03-25'), publicHolidays);
 * console.log(remainingDays); // Returns the count of remaining business days in March 2024
 */
export const calculateRemainingBusinessDays = (startDate, publicHolidays) => {
    const result = [];
    let currentDate = new Date(startDate);

    // Increment the currentDate to the next day to exclude the startDate itself
    currentDate.setDate(currentDate.getDate() + 1);

    // Loop until we reach the end of the month or have 7 business days
    while (currentDate.getMonth() === new Date(startDate).getMonth() && result.length < 7) {
        const dayOfWeek = currentDate.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6 && !isPublicHoliday(currentDate, publicHolidays)) {
            result.push(new Date(currentDate)); // Add a valid business day to the result
        }
        currentDate.setDate(currentDate.getDate() + 1);
    }
    return result.length;
}

/**
 * Calculates the number of business days (excluding weekends and public holidays) 
 * between two given dates.
 * 
 * @memberof Services
 * @function daysBetweenDatesExcludingWeekendsAndHolidays
 * @param {Date} date1 - The start date.
 * @param {Date} date2 - The end date.
 * @returns {number} - The number of business days between the two dates.
 * 
 * @example
 * const businessDays = daysBetweenDatesExcludingWeekendsAndHolidays(new Date('2024-03-01'), new Date('2024-03-10'));
 * console.log(businessDays); // Returns the count of business days between March 1st and March 10th
 */
export const daysBetweenDatesExcludingWeekendsAndHolidays = (date1, date2) => {
    let totalDays = 0;
    let currentDate = new Date(date1);

    currentDate.setDate(currentDate.getDate() + 1);

    while (currentDate <= date2) {
        const dayOfWeek = currentDate.getDay();

        // Check if it's not a weekend
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
            // Check if it's not a public holiday
            const isHoliday = publicHolidays.some(holiday =>
                holiday.getTime() === currentDate.getTime()
            );

            if (!isHoliday) {
                totalDays++;
            }
        }

        // Move to the next day
        currentDate.setDate(currentDate.getDate() + 1);
    }

    return totalDays;
}

/**
 * Calculates business days (excluding weekends) passed since a due date.
 *
 * @memberof Services
 * @function getBusinessDaysPastDue
 * @param {string} currentDateTimeStr - The current date/time string.
 * @param {string} dueDateStr - The due date string.
 * @returns {number} - The number of business days past due (negative if not yet due).
 */
export const getBusinessDaysPastDue = (currentDateTimeStr, dueDateStr) => {
    const currentDate = new Date(currentDateTimeStr);
    const dueDate = new Date(dueDateStr);
    currentDate.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);

    // If current date is before or equal to due date, return 0 or negative
    if (currentDate <= dueDate) {
        return 0;
    }

    let businessDays = 0;
    let tempDate = new Date(dueDate);
    tempDate.setDate(tempDate.getDate() + 1); // Start from day after due date

    while (tempDate <= currentDate) {
        const dayOfWeek = tempDate.getDay();
        // Skip weekends (0 = Sunday, 6 = Saturday)
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
            businessDays++;
        }
        tempDate.setDate(tempDate.getDate() + 1);
    }

    return businessDays;
};

/**
 * Calculates LPS (Late Payment Surcharge) for API based on business days past due.
 * - 0 days (not passed or equal): No LPS
 * - 1-3 business days past due: Half LPS
 * - More than 3 business days past due: Full LPS
 *
 * @memberof Services
 * @function calculateLpsForApi
 * @param {string|number} lpsValue - The LPS value (can have commas).
 * @param {string} currentDateTimeStr - The current date/time string.
 * @param {string} dueDateStr - The due date string.
 * @returns {number} - The calculated LPS value to send to API.
 */
export const calculateLpsForApi = (lpsValue, currentDateTimeStr, dueDateStr) => {
    const businessDaysPastDue = getBusinessDaysPastDue(currentDateTimeStr, dueDateStr);
    const lpsNum = lpsValue ? parseInt(String(lpsValue).replace(/,/g, '')) : 0;

    if (businessDaysPastDue <= 0) {
        // Due date not passed or equal - no LPS
        return 0;
    } else if (businessDaysPastDue <= 3) {
        // 1-3 business days past due - half LPS
        return Math.floor(lpsNum / 2);
    } else {
        // More than 3 business days past due - full LPS
        return lpsNum;
    }
};

/**
 * Calculates the geographical centroid (average center) of a set of latitude-longitude coordinates.
 * Uses spherical coordinates to compute the centroid.
 * 
 * @memberof Services
 * @function calculateCentroid
 * @param {Array<{ latitude: number, longitude: number }>} coordinates - An array of objects with latitude and longitude properties.
 * @returns {{ latitude: number, longitude: number }} - The calculated centroid coordinates.
 * @throws {Error} If no coordinates are provided.
 * 
 * @example
 * const centroid = calculateCentroid([{ latitude: 30.0, longitude: 70.0 }, { latitude: 32.0, longitude: 72.0 }]);
 * console.log(centroid); // { latitude: 31.0, longitude: 71.0 }
 */
export const calculateCentroid = (coordinates) => {
    if (coordinates.length === 0) {
        throw new Error('No coordinates provided');
    }

    let x = 0;
    let y = 0;
    let z = 0;

    coordinates.forEach(coord => {
        if (coord.latitude && coord.longitude) {

            const lat = coord.latitude * Math.PI / 180;
            const lon = coord.longitude * Math.PI / 180;

            x += Math.cos(lat) * Math.cos(lon);
            y += Math.cos(lat) * Math.sin(lon);
            z += Math.sin(lat);
        }
    });

    const total = coordinates.length;
    x /= total;
    y /= total;
    z /= total;

    const lon = Math.atan2(y, x);
    const hyp = Math.sqrt(x * x + y * y);
    const lat = Math.atan2(z, hyp);

    return {
        latitude: lat * 180 / Math.PI,
        longitude: lon * 180 / Math.PI
    };
}

/**
 * Filters a list of options based on an input value, considering multiple words for better matching.
 * 
 * @memberof Services
 * @function filterOptions
 * @param {string[]} options - Array of available options.
 * @param {{ inputValue: string }} param1 - Object containing the input value.
 * @returns {string[]} - Filtered options matching the input value.
 * 
 * @example
 * const options = ["New York", "New Jersey", "Los Angeles", "San Francisco"];
 * const filtered = filterOptions(options, { inputValue: "New" });
 * console.log(filtered); // ["New York", "New Jersey"]
 */
export const filterOptions = (options, { inputValue }) => {
    const inputWords = inputValue.trim().split(" ");
    let filteredOptions = options.filter((option) =>
        option.toLowerCase().startsWith(inputWords[0].toLowerCase())
    );
    if (inputWords.length > 1) {
        const remainingWords = inputWords.slice(1);
        filteredOptions = filteredOptions.filter((option) => {
            const optionWords = option.trim().split(" ");
            return remainingWords.every((word) =>
                optionWords.some((optionWord) =>
                    optionWord.toLowerCase().startsWith(word.toLowerCase())
                )
            );
        });
    }
    return filteredOptions;
};

/**
 * Formats a given date string into "MMM-YYYY" format (e.g., "Mar-2024").
 * 
 * @memberof Services
 * @function formatBillMonth
 * @param {string} dateString - The input date string.
 * @returns {string} - The formatted date as "MMM-YYYY".
 * 
 * @example
 * const formattedDate = formatBillMonth("2024-03-25");
 * console.log(formattedDate); // "Mar-2024"
 */
export const formatBillMonth = (dateString) => {
    // Create a new Date object from the input date string
    const date = new Date(dateString);

    // Define an array with month names
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    // Get the month and year from the date object
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    // Return the formatted string
    return `${month}-${year}`;
}

/**
 * Formats a date string in "YYYYMM" format into a human-readable "MMM YYYY" format.
 * 
 * @memberof Services
 * @function formatBillMonthYYYYMM
 * @param {string} dateString - A string in "YYYYMM" format.
 * @returns {string} - The formatted date in "MMM YYYY" format.
 * 
 * @example
 * const formattedDate = formatBillMonthYYYYMM("202403");
 * console.log(formattedDate); // "Mar 2024"
 */
export const formatBillMonthYYYYMM = (dateString) => {
    const year = dateString.slice(0, 4);  // Extract the year
    const monthNumber = dateString.slice(4);  // Extract the month
    const date = new Date(`${year}-${monthNumber}-01`);  // Create a date object

    const options = { year: 'numeric', month: 'short' };  // Formatting options
    return date.toLocaleDateString('en-US', options);  // Format the date
}

/**
 * Computes the previous month in "YYYYMM" format from a given input in the same format.
 * Handles year transitions correctly.
 * 
 * @memberof Services
 * @function getPreviousMonth
 * @param {number} input - The current month in "YYYYMM" format.
 * @returns {string} - The previous month in "YYYYMM" format.
 * 
 * @example
 * const prevMonth = getPreviousMonth(202403);
 * console.log(prevMonth); // "202402"
 */
export const getPreviousMonth = (input) => {
    let year = Math.floor(input / 100); // Extract the year
    let month = input % 100;            // Extract the month

    // Handle the previous month
    if (month === 1) {
        year--;  // Go to the previous year
        month = 12; // Set month to December
    } else {
        month--; // Just go to the previous month
    }

    // Return in YYYYMM format
    return `${year}${month.toString().padStart(2, '0')}`;
}

/**
 * Computes the next month in "YYYYMM" format from a given input in the same format.
 * Handles year transitions correctly.
 * 
 * @memberof Services
 * @function getNextMonth
 * @param {number} input - The current month in "YYYYMM" format.
 * @returns {string} - The next month in "YYYYMM" format.
 * 
 * @example
 * const nextMonth = getNextMonth(202312);
 * console.log(nextMonth); // "202401"
 */
export const getNextMonth = (input) => {
    let year = Math.floor(input / 100); // Extract the year
    let month = input % 100;            // Extract the month

    // Handle the next month
    if (month === 12) {
        year++;  // Go to the next year
        month = 1; // Set month to January
    } else {
        month++; // Just go to the next month
    }

    // Return in YYYYMM format
    return `${year}${month.toString().padStart(2, '0')}`;
}

/**
 * Checks if two months are exactly a specified number of months apart.
 * 
 * @memberof Services
 * @function isMonthsApart
 * @param {number} first - The first month in "YYYYMM" format.
 * @param {number} second - The second month in "YYYYMM" format.
 * @param {number} difference - The expected difference in months.
 * @returns {boolean} - Returns `true` if the months are exactly the given difference apart, otherwise `false`.
 * 
 * @example
 * const result = isMonthsApart(202301, 202303, 2);
 * console.log(result); // true
 */
export const isMonthsApart = (first, second, difference) => {
    let firstYear = Math.floor(first / 100);
    let firstMonth = first % 100;

    let secondYear = Math.floor(second / 100);
    let secondMonth = second % 100;

    // Calculate the total difference in months
    let monthDifference = (secondYear - firstYear) * 12 + (secondMonth - firstMonth);

    // Return true if the difference is exactly as specified
    return monthDifference === difference;
}
/**
 * Retrieves the full month name based on an offset from the current month.
 * 
 * @memberof Services
 * @function getMonthName
 * @param {number} X - The number of months to offset from the current month.
 * @returns {string} - The full name of the corresponding month.
 * 
 * @example
 * const monthName = getMonthName(1);
 * console.log(monthName); // "February" (if current month is January)
 */
export const getMonthName = (X) => ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][(new Date().getMonth() - X + 12) % 12];

/**
 * Retrieves the short name of the current month.
 * 
 * @memberof Services
 * @function getShortMonthName
 * @returns {string} - The abbreviated name of the current month (e.g., "Jan.", "Feb.").
 * 
 * @example
 * const shortMonth = getShortMonthName();
 * console.log(shortMonth); // "Mar." (if current month is March)
 */
export const getShortMonthName = () => ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'Jun.', 'Jul.', 'Aug.', 'Sep.', 'Oct.', 'Nov.', 'Dec.'][(new Date().getMonth() + 12) % 12];
/**
 * Returns the current and previous year in "YYYY - YYYY" format.
 * 
 * @memberof Services
 * @function getYear
 * @returns {string} - A string representing the previous and current year.
 * 
 * @example
 * const yearRange = getYear();
 * console.log(yearRange); // "2023 - 2024" (if current year is 2024)
 */
export const getYear = () => {
    let year = new Date().getFullYear();
    let previousYear = year - 1;
    return previousYear.toString() + " - " + year.toString()
}
/**
 * Retrieves the current year as a string.
 * 
 * @memberof Services
 * @function getCurrentYear
 * @returns {string} - The current year as a string.
 * 
 * @example
 * const currentYear = getCurrentYear();
 * console.log(currentYear); // "2024"
 */
export const getCurrentYear = () => {
    let year = new Date().getFullYear();
    return year.toString()
}
/**
 * Retrieves the current date in "DD, MonthName" format.
 * 
 * @memberof Services
 * @function getDate
 * @returns {string} - The current date formatted as "DD, MonthName".
 * 
 * @example
 * const currentDate = getDate();
 * console.log(currentDate); // "14, March" (if today is March 14th)
 */
export const getDate = () => {
    return new Date().getDate() + ', ' + getMonthName(0)
}
/**
 * Filters an array of data based on the specified duration.
 * 
 * @memberof Services
 * @function filterDataByDuration
 * @param {Array<{ Service: string, Pending: number, Attended: number, Total: number, Duration: string }>} data - The dataset to be filtered.
 * @param {string} duration - The duration value to filter the data.
 * @returns {Array<{ service: string, pending: number, attended: number, total: number }>} - Filtered data mapped to a specific structure.
 * 
 * @example
 * const dataset = [
 *   { Service: "Electricity", Pending: 5, Attended: 10, Total: 15, Duration: "Monthly" },
 *   { Service: "Water", Pending: 2, Attended: 5, Total: 7, Duration: "Weekly" }
 * ];
 * 
 * const filteredData = filterDataByDuration(dataset, "Monthly");
 * console.log(filteredData);
 * // [{ service: "Electricity", pending: 5, attended: 10, total: 15 }]
 */
export const filterDataByDuration = (data, duration) => {
    return data.filter((item) => item.Duration === duration).map(({ Service, Pending, Attended, Total }) => ({
        service: Service,
        pending: Pending,
        attended: Attended,
        total: Total,
    }));
};

export const allCFCServices = ['New Connections',  'Bill Installments', 'Due Date Extensions', 'Meter Change Order', 'Change of Attributes']

/**
 * Filters and merges service data based on duration.
 * @memberof Services
 * @param {Array} data - The data array.
 * @param {string} duration - The duration to filter by.
 * @param {Array} allServices - The list of all available services.
 * @returns {Array} Processed data ensuring all services are present.
 */
export const filterDataByDurationWithCFCServices = (data, duration, allServices) => {
    const serviceMapping = {
        'Change of Name': 'Change of Attributes',
        'Change of Load': 'Change of Attributes',
        'Change of Tariff': 'Change of Attributes',
        'Change Of Attributes': 'Change of Attributes',
    };

    const filteredData = data.filter((item) => item.Duration === duration);

    // Create a map for services that exist in filtered data
    const existingData = filteredData.reduce((acc, { Service, Pending, Attended, Total }) => {
        // Apply service mapping if necessary
        const mappedService = serviceMapping[Service] || Service;

        // If the service is already merged, sum the values
        if (!acc[mappedService]) {
            acc[mappedService] = {
                service: mappedService,
                pending: 0,
                attended: 0,
                total: 0,
            };
        }

        acc[mappedService].pending += Pending;
        acc[mappedService].attended += Attended;
        acc[mappedService].total += Total;

        return acc;
    }, {});

    // Ensure all services are present, with default values for missing services
    return allServices.map((service) => {
        return existingData[service] || {
            service: service,
            pending: 0,
            attended: 0,
            total: 0,
        };
    });
};

/**
 * Filters data by ripeness based on duration.
 * @memberof Services
 * @param {Array} data - The data array.
 * @param {string} duration - The duration to filter by.
 * @returns {Array} Filtered data.
 */
export const filterDataByRipUnripe = (data, duration) => {
    return data.filter((item) => item.Duration === duration).map(({ Service, Ripe, Unripe, Total }) => ({
        service: Service,
        ripe: Ripe,
        unripe: Unripe,
        total: Total,
    }));
};

/**
 * Fetches bill details from the API.
 * @memberof Services
 * @async
 * @param {string} token - Authorization token.
 * @param {string} referenceNo - Reference number.
 * @returns {Promise<Object>} The bill response.
 */


const getCompanyCode = (code) => {
    let codes;
    if (code.length == 14) {
        const companyCode = code.slice(2, 4);
        switch (companyCode) {
            case '12':
                codes = '12';
                break;

            case '13':
                codes = '13';
                break;

            case '14':
                codes = '14';
                break;

            case '15':
                codes = '15';
                break;

            case '26':
                codes = '26';
                break;

                 case '27':
                codes = '27';
                break;

            case '37':
                codes = '37';
                break;

            case '38':
                codes = '38';
                break;

            case '48':
                codes = '48';
                break;

            case '59':
                codes = '59';
                break;

            case '91':
                codes = '91';
                break;

            default:
                return '11';
        }
        return codes;
    } else {
        const companyCode = code.slice(1, 3);
        switch (companyCode) {
            case '12':
                codes = '12';
                break;

            case '13':
                codes = '13';
                break;

            case '14':
                codes = '14';
                break;

            case '15':
                codes = '15';
                break;

            case '26':
                codes = '26';
                break;

                case '27':
                codes = '27';
                break;

            case '37':
                codes = '37';
                break;

            case '38':
                codes = '38';
                break;

            case '48':
                codes = '48';
                break;

            case '59':
                codes = '59';
                break;

            case '91':
                codes = '91';
                break;

            default:
                return '11';
        }
        return codes;
    }
};

export const dBillAPI = async (token, referenceNo) => {
    var billResponse = {}
    try {
        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);

        const formdata = new FormData();
        formdata.append("refNo", referenceNo);
        formdata.append("companyCode", getCompanyCode(referenceNo));

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: formdata,
            redirect: "follow"
        };
        await fetch("https://eopsapi.pitc.com.pk/electricityNewConnection/dupBillingRep", requestOptions)
            .then((response) => response.json())
            .then((result) => {
                // if (result.status == true) {
                billResponse = result
                // }
            })
            .catch((error) => console.error(error));
    } catch (e) {
        console.log(e, 'error calling dbillAPI')
    }
    return billResponse;
}
/**
 * Fetches disconnection details from the API.
 * @memberof Services
 * @async
 * @param {string} customerId - Customer ID.
 * @param {string} officeCode - Office code.
 * @returns {Promise<Object>} The disconnection response.
 */ 
export const disconnectionDetails = async (token, customerId, companyCode , userCode) => {
  let disconnectionResponse = {};

  try {
    const myHeaders = new Headers();
    myHeaders.append("Authorization", token);
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify({
      "customerId": customerId,
      "officeCode": userCode === '10' ? '15' : companyCode
    });

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow"
    };

    const response = await fetch(cfpBaseUrl + "ibs/getDisconnectedConsumerDetails", requestOptions);
    const result = await response.json();
    disconnectionResponse = result;

  } catch (e) {
    console.log(e, "error calling disconnectionAPI");
  }

  return disconnectionResponse;
};

/**
 * Fetches consumer details from the IBS API.
 * @memberof Services
 * @async
 * @param {string} token - Authorization token.
 * @param {string} searchById - Consumer ID or Reference Number.
 * @returns {Promise<Object>} The consumer details response.
 */
export const getConsumerDetails = async (token, searchById) => {
    var consumerResponse = {};
    try {
        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);
        myHeaders.append("Content-Type", "application/json");

        const raw = JSON.stringify({ searchById });

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: raw,
            redirect: "follow",
        };

        await fetch(cfpBaseUrl + "ibs/getConsumerDetails", requestOptions)
            .then((response) => response.json())
            .then((result) => {
                consumerResponse = result;
            })
            .catch((error) => console.error(error));
    } catch (e) {
        console.log(e, "error calling getConsumerDetails API");
    }
    return consumerResponse;
};

export const paymentDetails = async (applicationNo, companyCode, token, code) => {
    let paymentResponse = { rows: [] };

    try {
        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);
        myHeaders.append("Content-Type", "application/json");

        const raw = JSON.stringify({
            searchById: applicationNo,
            officeCode: code === "10" ? "15" : String(companyCode),
        });

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: raw,
            redirect: "follow",
        };

        const response = await fetch(
            "http://175.107.33.109:8038/ibs/getConsumerPaymentSummary",
            requestOptions,
        );

        const result = await response.json();

        if (result?.status === "200") {
            paymentResponse = {
                ...result,
                rows: Array.isArray(result?.data) ? result.data : [],
            };
        } else if (result?.status === "400") {
            paymentResponse = {
                ...result,
                rows: [],
            };
        } else {
            paymentResponse = {
                ...result,
                rows: Array.isArray(result?.rows)
                    ? result.rows
                    : Array.isArray(result?.data)
                        ? result.data
                        : [],
            };
        }
    } catch (e) {
        console.log(e, "error calling paymentDetails API");
    }

    return paymentResponse;
};

export const discMeterDetails = async (referenceNo, companyCode, token , code) => {
    try {
        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);
        myHeaders.append("Content-Type", "application/json");

        const raw = JSON.stringify({
            searchById: referenceNo,
            officeCode: code === "10" ? "15" : String(companyCode),
        });

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: raw,
            redirect: "follow",
        };

        const response = await fetch(
            cfpBaseUrl + "ibs/getDisconnectedMeterProfile",
            requestOptions
        );

        const result = await response.json();
        return {
            ...result,
            rows: Array.isArray(result?.data)
                ? result.data
                : Array.isArray(result?.rows)
                    ? result.rows
                    : [],
        };
    } catch (error) {
        console.error("Error calling disconnection API:", error);
        return null;
    }
};


export const rcoMeterProfile = async (token,applicationId) => {

  const response = await fetch(
     cfpBaseUrl + "rco/getRCOMeterProfile",
    {
      method: "POST",
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        applicationId,
      }),
    }
  );

  const result = await response.json();
  return result; // { status, message, data, meterProfile }
};

/**
 * Extracts numbers from the right side of a string.
 * @memberof Services
 * @param {string} str - The input string.
 * @returns {string} The extracted number or an empty string if none found.
 */
export const getRightSideNumbers = (str) => {
    const match = str.match(/\d+$/); // This regex matches the numbers at the end of the string
    return match ? match[0] : '';   // If a match is found, return the numbers; otherwise, return an empty string
}
/**
 * Fetches pending payment details from the API.
 * @memberof Services
 * @async
 * @param {string} token - Authorization token.
 * @param {string} referenceNo - Reference number.
 * @returns {Promise<Object>} The pending payment response.
 */
export const pendingPayments = async (referenceNo, companyCode) => {
    var pendingPaymentResponse = {}
    try {
        const requestOptions = {
            method: "GET",
            redirect: "follow"
        };

        await fetch(`https://ibs-dashboard-data-api.pitc.com.pk/api/v1/common?apiName=eops_pend_payment_${companyCode}&P_REF_NO=${referenceNo}`, requestOptions)
            .then((response) => response.json())
            .then((result) => {
                pendingPaymentResponse = result
            })
            .catch((error) => console.error(error));
    } catch (e) {
        console.log(e, 'error calling pendingPaymentResponse')
    }
    return pendingPaymentResponse;
}

// export const dBillAPI = async (token, referenceNo) => {
//     var billResponse = {}
//     try {
//         const myHeaders = new Headers();
//         myHeaders.append("Authorization", token);

//         const formdata = new FormData();
//         formdata.append("refNo", referenceNo);

//         const requestOptions = {
//             method: "POST",
//             headers: myHeaders,
//             body: formdata,
//             redirect: "follow"
//         };

//         await fetch(cfpBaseUrl + "electricityNewConnection/dupBillingRep", requestOptions)
//             .then((response) => response.json())
//             .then((result) => {
//                 if (result.status) {
//                     billResponse = result
//                 }
//             })
//             .catch((error) => console.error(error));
//     } catch (e) {
//         console.log(e, 'error calling dbillAPI')
//     }
//     return billResponse;
// }

/**
 * Gets the start date of the month in DD-MMM-YY format.
 * If no date is provided, it returns the start date of the current month.
 *
 * @function getStartDateOfMonth
 * @memberof Services
 * @param {string|Date} [date] - The input date (optional).
 * @returns {string} - The formatted start date of the month (e.g., "01-Jan-24").
 */
export const getStartDateOfMonth = (date) => {
    if (!date) {
        const [day, month, year] = convertDateDDMMYY(new Date().setDate(1)).split('-')
        return `${day.length === 1 ? `0${day}` : day}-${month}-${year}`
    } else {
        const [day, month, year] = convertDateDDMMYY(new Date(date).setDate(1)).split('-')
        return `${day.length === 1 ? `0${day}` : day}-${month}-${year}`
    }
}


/**
 * Gets the date from a specific number of months back from the given date.
 *
 * @function getMonthsBack
 * @memberof Services
 * @param {string} dateString - The date in DD-MMM-YY format.
 * @param {number} noOfMonths - Number of months to go back.
 * @returns {string} - The new date in DD-MMM-YY format.
 */
export const getMonthsBack = (dateString, noOfMonths) => {
    const monthsArray = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const [day, month, year] = dateString.split('-')
    let currentIndex = monthsArray.indexOf(month)
    let newIndex = currentIndex - noOfMonths
    if (newIndex >= 0) {
        return `${day}-${monthsArray[newIndex]}-${year}`
    } else {
        let currentYear = parseInt(year)
        let newYear = currentYear - 1
        return `${day}-${monthsArray[(monthsArray.length - 1) - (noOfMonths - 1)]}-${newYear}`
    }
}

/**
 * Converts an input number, numeric string, or an array of numbers to millions.
 *
 * @function convertToMillions
 * @memberof Services
 * @param {number|string|Array<number|string>} input - The input value(s).
 * @returns {number|Array<number>} - The value(s) converted to millions.
 * @throws {Error} - If the input is not a number, numeric string, or an array of such values.
 */
export const convertToMillions = (input) => {
    if (Array.isArray(input)) {
        return input.map(num => Number((Number(num) / 1000000).toFixed(2)));
    } else if (typeof input === 'number') {
        return Number((input / 1000000).toFixed(2));
    } else if (typeof input === 'string' && !isNaN(Number(input))) {
        return Number((Number(input) / 1000000).toFixed(2));
    } else {
        throw new Error('Invalid input: must be an array, a number, or a numeric string');
    }
};

/**
 * Calculates the main figure for TD (Transmission & Distribution) losses.
 * It finds the difference between the last two values in the array.
 *
 * @function calculateTDMainFigure
 * @memberof Services
 * @param {Array<{value: number}>} array - The input array with values.
 * @returns {string} - The computed TD loss value as a string (fixed to 2 decimal places).
 */
export const calculateTDMainFigure = (array) => {
    let aggregateTD = 0
    let index = array.length - 1
    if (array.length > 1) {
        aggregateTD = (parseFloat(array[index - 1].value) - parseFloat(array[index].value)).toFixed(2)
    }
    else {
        aggregateTD = parseFloat(array[index].value).toFixed(2)
    }
    return aggregateTD
}

/**
 * Calculates the main recovery figure by finding the difference between the last two values in the array.
 *
 * @function calculateRecoveryMainFigure
 * @memberof Services
 * @param {Array<{value: number}>} array - The input array with values.
 * @returns {string} - The computed recovery value as a string (fixed to 2 decimal places).
 */
export const calculateRecoveryMainFigure = (array) => {
    let aggregateRecovery = 0
    let index = array.length - 1
    if (array.length > 1) {
        aggregateRecovery = (parseFloat(array[index - 1].value) - parseFloat(array[index].value)).toFixed(2)
    } else {
        aggregateRecovery = parseFloat(array[index].value).toFixed(2)
    }
    return aggregateRecovery
}

/**
 * Calculates the main ATC (Aggregate Technical & Commercial) figure.
 * It finds the difference between the last two values in the array.
 *
 * @function calculateATCMainFigure
 * @memberof Services
 * @param {Array<{value: number}>} array - The input array with values.
 * @returns {string} - The computed ATC value as a string (fixed to 2 decimal places).
 */
export const calculateATCMainFigure = (array) => {
    let aggregateATC = 0
    let index = array.length - 1
    if (array.length > 1) {
        aggregateATC = (parseFloat(array[index - 1].value) - parseFloat(array[index].value)).toFixed(2)
    } else {
        aggregateATC = parseFloat(array[index].value).toFixed(2)
    }
    return aggregateATC
}

/**
 * Transforms an array of input data, grouping it by officeCode and splitting it into two categories:
 * - `firstYear`: Data before the current year
 * - `secondYear`: Data from the current year onwards
 *
 * @function transformData
 * @memberof Services
 * @param {Array<{ officeCode: string, monthYear: string }>} inputArray - The input data array.
 * @returns {Array<{ officeCode: string, separateYearData: { firstYear: Array<Object>, secondYear: Array<Object> } }>}
 * - An array of objects containing grouped data.
 */
export const transformData = (inputArray) => {
    const result = {};

    const currentYear = new Date().getFullYear() - 2000;

    inputArray.forEach(item => {
        const officeCode = item.officeCode;

        if (!result[officeCode]) {
            result[officeCode] = {
                firstYear: [],
                secondYear: [],
            };
        }

        const yearPrefix = item.monthYear.split('-')[1];
        const yearCategory = parseInt(yearPrefix) >= currentYear ? 'secondYear' : 'firstYear';

        result[officeCode][yearCategory].push(item);
    });

    // Convert the result object to an array of objects
    const finalResult = Object.keys(result).map(officeCode => ({
        officeCode,
        separateYearData: result[officeCode],
    }));

    return finalResult;
}

/**
 * Separates data objects by year and assigns ordinal names (first, second, third, etc.).
 *
 * @function separateObjectsByYear
 * @memberof Services
 * @param {Array<{ monthYear: string }>} data - The input array of objects containing a monthYear property.
 * @returns {Object} An object with keys as ordinal year names and values as arrays of matching objects.
 */
export const separateObjectsByYear = (data) => {
    const separatedData = {};

    // Define an array of ordinal numbers
    const ordinals = ['first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth'];

    // Iterate through each object in the array
    data.forEach(obj => {
        // Extract the year from the monthYear property
        const yearPrefix = obj.monthYear.split('-')[1];
        if (parseInt(yearPrefix) > 23) {

            // Use the yearPrefix to get the corresponding ordinal
            const ordinal = ordinals[parseInt(yearPrefix) - 24] || 'unknown';

            // If the ordinal is not already a key in separatedData, create an empty array for it
            if (!separatedData[ordinal + 'Year']) {
                separatedData[ordinal + 'Year'] = [];
            }

            // Push the object into the array corresponding to its ordinal year
            separatedData[ordinal + 'Year'].push(obj);
        }
    });

    return separatedData;
}

/**
 * Groups an array of data objects by office code.
 *
 * @function groupByOfficeCode
 * @memberof Services
 * @param {Array<{ officeCode: string }>} data - The input data array.
 * @returns {Array<{ officeCode: string, defectiveMetersData: Array }>} Grouped data by office code.
 */
export const groupByOfficeCode = (data) => {
    const result = [];

    const officeCodeMap = new Map();

    data.forEach(item => {
        const { officeCode } = item;

        if (!officeCodeMap.has(officeCode)) {
            officeCodeMap.set(officeCode, {
                officeCode: officeCode,
                defectiveMetersData: []
            });
        }

        officeCodeMap.get(officeCode).defectiveMetersData.push(item);
    });

    officeCodeMap.forEach(value => {
        result.push(value);
    });

    return result;
}

/**
 * @function groupByOfficeCodeMC
 * @description Groups an array of objects by `officeCode`, specifically for meter change data.
 * @memberof Services
 * @param {Array<Object>} data - The array of objects containing `officeCode` and other properties.
 * @returns {Array<Object>} - An array where each object contains `officeCode` and corresponding `meterChangeData`.
 */
export const groupByOfficeCodeMC = (data) => {
    const result = [];

    // Create a map to store data for each officeCode
    const officeCodeMap = new Map();

    // Iterate through the data array
    data.forEach(item => {
        const { officeCode } = item;

        // If officeCode is not in the map, create a new entry
        if (!officeCodeMap.has(officeCode)) {
            officeCodeMap.set(officeCode, {
                officeCode: officeCode,
                meterChangeData: []
            });
        }

        // Add the current item to the corresponding officeCode entry
        officeCodeMap.get(officeCode).meterChangeData.push(item);
    });

    // Convert the map values to an array and push to the result
    officeCodeMap.forEach(value => {
        result.push(value);
    });

    return result;
}

/**
 * @function getFullMonthName
 * @description Converts a short month name (e.g., 'Jan') to its full month name (e.g., 'January').
 * @memberof Services
 * @param {string} shortName - The abbreviated month name (e.g., 'Jan', 'Feb', etc.).
 * @returns {string} - The full month name, or 'Month' if the abbreviation is not recognized.
 */
export const getFullMonthName = (shortName) => {
    switch (shortName) {
        case 'Jan':
            return 'January'
        case 'Feb':
            return 'February'
        case 'Mar':
            return 'March'
        case 'Apr':
            return 'April'
        case 'May':
            return 'May'
        case 'Jun':
            return 'June'
        case 'Jul':
            return 'July'
        case 'Aug':
            return 'August'
        case 'Sep':
            return 'September'
        case 'Oct':
            return 'October'
        case 'Nov':
            return 'November'
        case 'Dec':
            return 'December'
        default:
            return 'Month'
    }
}

/**
 * @function formatDate
 * @description Formats a given date string into `DD-MM-YYYY HH:mm` format.
 * @memberof Services
 * @param {string} dateString - The date string to format.
 * @returns {string} - The formatted date string.
 */
export const formatDate = (dateString) => {
    const date = new Date(dateString);

    // Get components
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    // Format as desired
    return `${day}-${month}-${year} ${hours}:${minutes}`;
}


/**
 * @function mergeWithDefaultData
 * @description Merges API data with default data based on `Service` and `Duration` keys.
 * @memberof Services
 * @param {Array<Object>} apiData - The array of objects from API.
 * @returns {Array<Object>} - A merged array containing API data with default values for missing services.
 */
export const mergeWithDefaultData = (apiData) => {
    // Normalize API service names to handle casing inconsistencies
    const serviceNameNormalization = {
        'Change Of Attributes': 'Change of Attributes',
    };
    const defaultData = createDefaultData();
    const dataMap = {};
    defaultData.forEach(item => {
        dataMap[`${item.Service}-${item.Duration}`] = item;
    });
    // Include ALL services from API data (not just defaultServices)
    apiData.forEach(item => {
        const normalizedService = serviceNameNormalization[item.Service] || item.Service;
        const key = `${normalizedService}-${item.Duration}`;
        if (dataMap[key]) {
            dataMap[key] = { ...item, Service: normalizedService, Pending: (dataMap[key].Pending || 0) + (item.Pending || 0), Attended: (dataMap[key].Attended || 0) + (item.Attended || 0), Total: (dataMap[key].Total || 0) + (item.Total || 0) };
        } else {
            dataMap[key] = { ...item, Service: normalizedService };
        }
    });
    return Object.values(dataMap);
};

/**
 * @function createDefaultData
 * @description Creates default data for various services with predefined durations.
 * @memberof Services
 * @returns {Array<Object>} - An array containing default data objects.
 */

export const createDefaultData = () => {
    const defaultData = [];
    defaultServices.forEach(service => {
        defaultDurations.forEach(duration => {
            defaultData.push({
                Service: service,
                Total: 0,
                Attended: 0,
                Pending: 0,
                Duration: duration
            });
        });
    });
    return defaultData;
};

/**
 * @function createDefaultDataCFCServicesCards
 * @description Creates default data for CFC Services Cards with predefined durations.
 * @memberof Services
 * @returns {Array<Object>} - An array containing default CFC services data objects.
 */
export const createDefaultDataCFCServicesCards = () => {
    const defaultData = [];
    defaultServicesCFC.forEach(service => {
        defaultDurations.forEach(duration => {
            defaultData.push({
                Service: service,
                Total: 0,
                Attended: 0,
                Pending: 0,
                Duration: duration
            });
        });
    });
    return defaultData;
};

/**
 * @function mergeWithDefaultCFCData
 * @description Merges API data with default CFC service data based on `Service` and `Duration` keys.
 * @memberof Services
 * @param {Array<Object>} apiData - The array of objects from the API.
 * @returns {Array<Object>} - A merged array containing API data with default values for missing services.
 */
export const mergeWithDefaultCFCData = (apiData) => {
    // Normalize API service names to handle casing inconsistencies
    const serviceNameNormalization = {
        'Change Of Attributes': 'Change of Attributes',
    };
    const defaultData = createDefaultDataCFCServicesCards();
    const dataMap = {};
    defaultData.forEach(item => {
        dataMap[`${item.Service}-${item.Duration}`] = item;
    });
    // Include ALL services from API data (not just defaultServices)
    apiData.forEach(item => {
        const normalizedService = serviceNameNormalization[item.Service] || item.Service;
        const key = `${normalizedService}-${item.Duration}`;
        if (dataMap[key]) {
            dataMap[key] = { ...item, Service: normalizedService, Pending: (dataMap[key].Pending || 0) + (item.Pending || 0), Attended: (dataMap[key].Attended || 0) + (item.Attended || 0), Total: (dataMap[key].Total || 0) + (item.Total || 0) };
        } else {
            dataMap[key] = { ...item, Service: normalizedService };
        }
    });
    return Object.values(dataMap);
};

/**
 * @function downloadBill
 * @description Opens a new tab with the bill URL based on the reference number.
 * @memberof Services
 * @param {string} refNo - The reference number for the bill.
 */
// export const downloadBill = async (refNo) => {
//     console.log(refNo,"Reference Number")
//     if (parseInt(refNo.slice(0, 2)) > 23 && parseInt(refNo.slice(0, 2)) != 26) {
//     console.log(refNo,"Reference Number1")

//         let url = 'https://bill.pitc.com.pk/mepcobill/industrial?refno=' + refNo
//         window.open(url, '_blank');
//     } else {
//     console.log(refNo,"Reference Number2")
//         let url = 'https://bill.pitc.com.pk/mepcobill/general?refno=' + refNo
//         window.open(url, '_blank');
//     }
// }
export const downloadBill = async (refNo) => {


    let url = 'https://bill.pitc.com.pk/pitc/route_access/' + refNo
    window.open(url, '_blank');
}

/**
 * @function downloadBillAdjBill
 * @description Opens a new tab with the adjusted bill URL based on various conditions.
 * @memberof Services
 * @param {number} applicationProgressId - The progress ID of the application.
 * @param {boolean} isBillPosted - Whether the bill has been posted.
 * @param {string} billAdjTrackingId - The tracking ID for the adjusted bill.
 * @param {string} refNo - The reference number for the bill.
 */
export const downloadBillAdjBill = async (applicationProgressId, isBillPosted, billAdjTrackingId, refNo) => {
    if (applicationProgressId === 4 && isBillPosted) {
        if (parseInt(refNo.slice(0, 2)) > 23 && parseInt(refNo.slice(0, 2)) != 26) {
            let url = 'http://billadj.pitc.com.pk/mepco/ibill.php?trackid=' + billAdjTrackingId
            window.open(url, '_blank');
        } else {
            let url = 'http://billadj.pitc.com.pk/mepco/print_bill.php?trackid=' + billAdjTrackingId
            window.open(url, '_blank');
        }
    }
}

/**
 * @function getCurrentDateFormatted
 * @description Formats a given date into `DD-MMM-YY` format (e.g., `14-Mar-25`).
 * @memberof Services
 * @param {Date} date - The date to format.
 * @returns {string} - The formatted date string.
 */
export const getCurrentDateFormatted = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const options = { month: 'short' };
    const month = date.toLocaleString('en-US', options);
    const year = String(date.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
}


/**
 * @function officeName
 * @description Returns the office name based on the given office code.
 * @memberof Services
 * @param {string|number} code - The office code.
 * @param {Array<Object>} subDivisions - The array of subdivisions.
 * @returns {string} - The office name.
 */
export const officeName = (code, subDivisions) => {
    let officeName = ''
    if (code) {
        if (code.toString().length == 2) {
            officeName = companyName(code == "10" ? "15" : code.toString())
        } else if (code.toString().length == 3) {
            officeName = circleName(code == "10" ? "151" : code, subDivisions) + ' Circle'
        } else if (code.toString().length == 4) {
            officeName = divisionName(code == "10" ? "1517" : code, subDivisions) + ' Division'
        } else if (code.toString().length == 5) {
            officeName = subdivisionName(code == "10" ? "15171" : code, subDivisions) + ' Sub Division'
        }
    }
    return officeName
}


/**
 * @function calculateInstallments
 * @description Splits the total amount into equal installments, adjusting for any remainder.
 * @memberof Services
 * @param {number} totalAmount - The total amount to be split into installments.
 * @param {number} numberOfInstallments - The number of installments.
 * @returns {Array<number>} - An array of installment amounts.
 */
export const calculateInstallments = (totalAmount, numberOfInstallments) => {
    const baseInstallment = Math.floor(totalAmount / numberOfInstallments);
    const remainder = totalAmount % numberOfInstallments;
    const installments = Array(numberOfInstallments).fill(baseInstallment);
    for (let i = 0; i < remainder; i++) {
        installments[i] += 1;
    }
    return installments;
}

/**
 * @function calculateInstallmentsForRco
 * @description Calculates installments for RCO, ensuring the first installment is half of the total amount.
 * @memberof Services
 * @param {number} totalAmount - The total amount to be split into installments.
 * @param {number} numberOfInstallments - The number of installments.
 * @throws {Error} - If the number of installments is less than 1.
 * @returns {Array<number>} - An array of installment amounts.
 */
export const calculateInstallmentsForRco = (totalAmount, numberOfInstallments) => {
    if (numberOfInstallments < 1) {
        throw new Error("Number of installments must be at least 1");
    }

    // First installment is half of the total amount
    const firstInstallment = Math.ceil(totalAmount / 2);

    // Remaining amount to be divided among remaining installments
    const remainingAmount = totalAmount - firstInstallment;
    const remainingInstallmentsCount = numberOfInstallments - 1;

    // If only one installment, return the full amount as the first installment
    if (remainingInstallmentsCount === 0) {
        return [firstInstallment];
    }

    // Calculate base installment and remainder for the remaining installments
    const baseInstallment = Math.floor(remainingAmount / remainingInstallmentsCount);
    const remainder = remainingAmount % remainingInstallmentsCount;

    // Create an array for the installments
    const installments = Array(remainingInstallmentsCount).fill(baseInstallment);

    // Distribute the remainder across the first few remaining installments
    for (let i = 0; i < remainder; i++) {
        installments[i] += 1;
    }

    // Add the first installment to the beginning of the array
    installments.unshift(firstInstallment);

    return installments;
}

/**
 * @function getPermissions
 * @description Fetches permissions based on the application type.
 * @memberOf Services
 * @param {string} token - Authorization token for the API request.
 * @param {string} applicationType - The type of application for which permissions are needed.
 * @returns {Promise<Array>} A promise that resolves to an array of permission objects.
 */
export const getPermissions = async (token, applicationType) => {
    let provisions = [];
    try {

        const myHeaders = new Headers();
        myHeaders.append('Authorization', token);

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            redirect: "follow"
        };

        await fetch(cfpBaseUrl + "actionables/getRanges", requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (result) {
                    provisions = result.message
                    provisions = provisions.filter((item) => item.id == applicationType)
                }
            })
            .catch((error) => console.error(error));
    }
    catch (e) {
        console.log(e);
    }
    return provisions
}


/**
 * @function getRelevantCFCCode
 * @description Fetches the relevant CFC (Customer Facilitation Center) code based on office details.
 * @memberOf Services
 * @param {string} token - Authorization token for the API request.
 * @param {string} code - The office code.
 * @param {string} level - The office level.
 * @returns {Promise<string|null>} A promise that resolves to the CFC code or null if not found.
 */
export const getRelevantCFCCode = async (token, code, level) => {
    let cfcCode = null;
    try {

        const myHeaders = new Headers();
        myHeaders.append('Authorization', token);
        myHeaders.append("Content-Type", "application/json");


        const raw = JSON.stringify({
            "officeCode": code,
            "officeLevel": level
        });

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: raw,
            redirect: "follow"
        };


        await fetch(cfpBaseUrl + "actionables/getReleventCFCDetails", requestOptions)
            .then((response) => response.json())
            .then((result) => {
                if (result) {
                    cfcCode = result.message[0].cfcId
                }
            })
            .catch((error) => console.error(error));
    }
    catch (e) {
        console.log(e);
    }
    return cfcCode
}

/**
 * @function capitalizeWords
 * @description Capitalizes the first letter of each word in a given text.
 * @memberOf Services
 * @param {string} text - The text to be capitalized.
 * @returns {string} The capitalized text.
 */
export const capitalizeWords = (text) => {
    if (!text) return '';

    return text
        .toLowerCase()
        .split(/[\s.]+/) // Split by both space and full stop
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

/**
 * @function getPhase
 * @description Determines the phase of electricity based on the given load.
 * @memberOf Services
 * @param {number} load - The electrical load.
 * @returns {string} The phase type.
 */
export const getPhase = (load) => {
    if (parseInt(load) < 5) {
        return "Single Phase";
    } else if (load >= 5 && load < 20) {
        return "3 Phase"
    } else if (load >= 20 && load < 500) {
        return "MDI (LT)(CT)"
    } else if (load >= 500) {
        return "MDI (HT)(CT/PT)"
    }
}

/**
 * @function getMeterType
 * @description Determines the type of meter based on the connection category, phase, and meter information.
 * @memberOf Services
 * @param {string} connectionCatageroy - The category of connection.
 * @param {string} meterTypes - The type of meter.
 * @param {string} phase - The phase type.
 * @param {number} meterInfoLength - The length of meter information.
 * @returns {string} The determined meter type.
 */
export const getMeterType = (connectionCatageroy, meterTypes, phase, meterInfoLength,) => {
    if ((connectionCatageroy === "01" && phase === "3-PHASE") ||
        (connectionCatageroy === "01" && meterInfoLength == "2") ||
        (meterTypes === "AMR" && phase === "3-PHASE") ||
        (meterTypes === "AMR" && meterInfoLength == "2") ||
        (meterTypes === "MMR" && phase === "3-PHASE" && connectionCatageroy === "01") ||
        (meterTypes === "MMR" && meterInfoLength == "2" && connectionCatageroy === "01")) {
        return "Smart Meter  ";
    }
    else if ((connectionCatageroy === "00" && phase === "3-PHASE") ||
        (connectionCatageroy === "00" && meterInfoLength == "2") ||
        (connectionCatageroy === null && meterInfoLength == "2") ||
        (connectionCatageroy === "" && meterInfoLength == "2") ||
        (connectionCatageroy === "" && phase === "3-PHASE") ||
        (connectionCatageroy === null && phase === "3-PHASE") ||
        (connectionCatageroy === "" && meterTypes === "MMR" && phase === "3-PHASE") ||
        (connectionCatageroy === null && meterTypes === "MMR" && phase === "3-PHASE") ||
        (connectionCatageroy === "" && meterTypes === "MMR" && meterInfoLength == "2") ||
        (connectionCatageroy === "" && meterTypes === "NORMAL" && phase === "3-PHASE") ||
        (connectionCatageroy === null && meterTypes === "NORMAL" && phase === "3-PHASE") ||
        (connectionCatageroy === "" && meterTypes === "NORMAL" && meterInfoLength == "2") ||
        (connectionCatageroy === null && meterTypes === "NORMAL" && meterInfoLength == "2") ||
        (meterTypes === "NORMAL" && phase === "3-PHASE") ||
        (meterTypes === "NORMAL" && meterInfoLength == "2") ||
        (meterTypes === "MMR" && phase === "3-PHASE") ||
        (meterTypes === "MMR" && meterInfoLength == "2")) {
        return "Normal Meter "
    }
    else {
        return "Normal Meter"
    }
}

/**
 * @function getReconnectionFee
 * @description Determines the reconnection fee based on arrears at disconnection.
 * @memberOf Services
 * @param {Array} array - Array of reconnection fee details.
 * @param {number} arrearsAtDisconn - The amount of arrears at the time of disconnection.
 * @returns {number|null} The reconnection fee or null if no match is found.
 */
export const getReconnectionFee = (array, arrearsAtDisconn) => {
    for (let item of array) {
        const { arrearAmountFrom, arrearAmountTo, reconnectionFee } = item;

        if (
            (arrearAmountFrom === null || arrearsAtDisconn >= arrearAmountFrom) &&
            (arrearAmountTo === null || arrearsAtDisconn <= arrearAmountTo)
        ) {
            return reconnectionFee;
        }
    }

    // Return null or a default value if no match is found
    return null;
}

/**
 * @function getFixedChargesForRco
 * @description Calculates the fixed charges for RCO based on tariff and phase.
 * @memberOf Services
 * @param {string} tariff - The tariff type.
 * @param {string} phase - The phase type.
 * @returns {number} The calculated fixed charges.
 */
export const getFixedChargesForRco = (tariff, phase) => {
    let fixedCharges = 0;
    if (tariff == 'A-1' || tariff == 'A-2' || tariff == 'A-2') {
        if (phase == 'Single Phase') {
            fixedCharges = 200
        }
    }
    return fixedCharges
}

/**
 * @function toTitleCase
 * @description Converts a given string to title case.
 * @memberOf Services
 * @param {string} str - The string to be converted.
 * @returns {string} The string in title case.
 */
export const toTitleCase = (str) => {
    return str.replace(/\w\S*/g, function (word) {
        return word.charAt(0).toUpperCase() + word.substr(1).toLowerCase();
    });
}

/**
* Formats a numeric value to millions with two decimal places.
*
* @function formatToMillions
* @param {number} value - The numeric value to be formatted.
* @returns {string} - The formatted value in millions with two decimal places.
*
* @example
* formatToMillions(5000000); // Returns "5.00"
* formatToMillions(125000000); // Returns "125.00"
*
* @description
* - Divides the input value by 1,000,000 to convert it to millions.
* - Uses `Intl.NumberFormat` to format the result with two decimal places.
* - Ensures the formatted output adheres to the 'en-US' locale.
*/
export const formatToMillions = (value) => {
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2, // Fixed to 2 decimal places
        maximumFractionDigits: 2,
    }).format(value / Math.pow(10, 6)); // Convert value to millions
}

/**
 * Formats a number by adding commas as thousand separators and removing any fractions.
 * Returns a dash ("-") if the input is invalid or empty.
 *
 * @function
 * @memberof Services
 * @param {string|number|null} value - The input value to format.
 * @returns {string} The formatted number with commas or "-" if the value is invalid.
 */
export const formatNumberWithoutFraction = (value) => {
    if (value === undefined || value === null || value === "") {
        return "-";
    }
    const num = parseFloat(value);
    return isNaN(num) ? "-" : num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};


/**
 * Formats an SDIV code by ensuring it has a minimum length of 5 characters.
 * If the code is shorter than 5 characters, it pads the end with zeros.
 *
 * @function formatSDIVCode
 * @param {string} code - The SDIV code to be formatted.
 * @returns {string} - The formatted SDIV code with a minimum length of 5 characters.
 *
 * @example
 * formatSDIVCode("123"); // Returns "12300"
 * formatSDIVCode("98765"); // Returns "98765"
 *
 * @description
 * - If the input `code` is less than 5 characters, it pads the end with "0" until it reaches a length of 5.
 * - If the input `code` is already 5 or more characters, it returns the code as is.
 */
export const formatSDIVCode = (code) => {
    return code.length < 5 ? code.padEnd(5, "0") : code;
};


/**
 * Determines the highlight color for a row based on the number of trailing zeros in the given code.
 *
 * @function shouldHighlightRow
 * @param {string} code - The code to check for trailing zeros.
 * @returns {string|null} - The corresponding highlight color or `null` if no highlighting is needed.
 *
 * @example
 * shouldHighlightRow("12340"); // Returns "#ccecf9" (1 trailing zero)
 * shouldHighlightRow("123400"); // Returns "#c5ddf9" (2 trailing zeros)
 * shouldHighlightRow("1234000"); // Returns "#8ac6f7" (3 trailing zeros)
 * shouldHighlightRow("123456"); // Returns null (no trailing zeros)
 *
 * @description
 * - Checks the number of trailing zeros in the `code`.
 * - Returns a specific color based on the count of trailing zeros:
 *   - 1 trailing zero → `#ccecf9`
 *   - 2 trailing zeros → `#c5ddf9`
 *   - 3 trailing zeros → `#8ac6f7`
 * - If there are no trailing zeros or more than 3, it returns `null` (no highlight).
 */
export const shouldHighlightRow = (code) => {
    const trailingZeros = code.match(/0+$/)?.[0]?.length || 0;
    if (trailingZeros === 1) {
        return "#ccecf9";
    } else if (trailingZeros === 2) {
        return "#c5ddf9";
    } else if (trailingZeros === 3) {
        return "#8ac6f7";
    }
    return null;
};


/**
 * Formats a number with commas as thousands separators and ensures two decimal places.
 *
 * @function formatWithCommasAndFixed
 * @param {number|string} value - The value to format. Can be a number or a numeric string.
 * @returns {string} - The formatted number with commas and two decimal places. Returns "0.00" if input is not a valid number.
 *
 * @example
 * formatWithCommasAndFixed(1234567.89); // Returns "1,234,567.89"
 * formatWithCommasAndFixed("9876543.2"); // Returns "9,876,543.20"
 * formatWithCommasAndFixed("abc"); // Returns "0.00" (invalid input)
 * formatWithCommasAndFixed(null); // Returns "0.00"
 *
 * @description
 * - Checks if the input is a valid number. If not, returns "0.00".
 * - Converts the input to a floating-point number.
 * - Formats the number with thousands separators and exactly two decimal places.
 */
export const formatWithCommasAndFixed = (value) => {
    if (isNaN(value)) {
        return "0.00"; // Return default value if input is not a valid number
    }

    const number = parseFloat(value); // Ensure the value is a number
    return number.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}


/**
  * Throttle function to limit the execution of a function.
  * @param {Function} func - Function to be throttled.
  * @param {number} limit - Time in milliseconds.
  * @returns {Function} Throttled function.
  */
export const throttle = (func, limit) => {
    let lastFunc;
    let lastRan;
    return function (...args) {
        if (!lastRan) {
            func.apply(this, args);
            lastRan = Date.now();
        } else {
            clearTimeout(lastFunc);
            lastFunc = setTimeout(() => {
                if ((Date.now() - lastRan) >= limit) {
                    func.apply(this, args);
                    lastRan = Date.now();
                }
            }, limit - (Date.now() - lastRan));
        }
    };
};

/**
 * Debounce function to delay execution of a function.
 * @param {Function} func - Function to be debounced.
 * @param {number} delay - Time in milliseconds.
 * @returns {Function} Debounced function.
 */
export const debounce = (func, delay) => {
    let debounceTimer;
    return function (...args) {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => func.apply(this, args), delay);
    };
};


// colors.ts

export const colors = {
    primaryBlue: "#006ad1",
    hoverBlue: "#d5e5ff",
    hoverTextBlue: "#1f7cd6",
    successGreen: "#4caf50",
    warningOrange: "#eb7b30",
    white: "#ffffff",
    grayBorder: "#e0e0e0",
    errorColor: '#f73232',
};


export const permissionsVariables = {
    powerComplaints: "Power Complaints",
    reports: "Reports",
    generalServices: "General Services",
    adjustLastBill: 'Adjust Last Bill',
    amendDuplicateBill:"Amend Duplicate Bill",
    billInstallments : 'Installments',
    reConnection:"Reconnection",
    tenantCNICUpdation:"Tenant CNIC Updation",
    dueDateExtension: "Due Date Extension",
    newConnection: "New Connection",
    adjustLastBill : 'Adjust Last Bill',
    billingAndCollection: "Billing and Collection",
    staffPerformance: "Staff Performance",
    commercialOperations: 'Commercial Operations',
    operationalPerformance: 'Operational Performance',
    organizationManagement: "Organization Management",
    accessManagement: "Access Management",
    partPayment : 'Part Payment',
    changeOfAttributes: "Change of Attributes",
    meterChangeRequest: "Meter Change Order",
    mco:"Meter Change Order",
    duplicateBill:"Duplicate Bill"

};
